import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useCampaignDetails } from './hooks/useCampaignDetails';
import { useCampaignBookings } from './hooks/useCampaignBookings';
import { Skeleton } from '@round/ui-kit';
import styles from './CampaignDetails.module.css';
import SectionTitle from '../../../../ui/SectionTitle/SectionTitle';
import BookingSummary from '../../booking/components/BookingSummary/BookingSummary';
import { calculateBookingsTotals } from '../../booking/helpers';
import { Helmet } from 'react-helmet';
import useCampaignTiktokPostStats from './hooks/useCampaignTitkokPostStats';
import TiktokPostStatsTable, { TiktokPostStatsTableRow } from './CampaignPostStats/TiktokPostStatsTable';
import useAbortableEffect from '../../../../hooks/useAbortableEffect';
import { ReactComponent as TiktokIcon } from '../../../../assets/TiktokIcon.svg';
import { ReactComponent as InstagramIcon } from '../../../../assets/InstagramIcon.svg';
import useCampaignInstagramPostStats from './hooks/useCampaignInstagramPostStats';
import InstagramPostStatsTable, { InstagramPostStatsTableRow } from './CampaignPostStats/InstagramPostStatsTable';
import { formatNumberToKNotation, numberWithCommas } from '../../../../utils/valueFormatHelpers';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import uniq from 'lodash/uniq';
import {
    sortInstagramPostStatsTableRows,
    sortTiktokPostStatsTableRows,
} from './CampaignPostStats/PostStatsTable.helpers';
import { useInfluencerImages } from '../../../../hooks/useInfluencerImages';

const HeadlineStatsCard = ({
    value,
    label,
    loading,
}: {
    value: string | number | null;
    label: string;
    loading?: boolean;
}) =>
    loading ? (
        <Skeleton className={styles.cardSkeleton} />
    ) : (
        <div className={styles.headlineStatsCard}>
            <p className={styles.headlineStatsValue}>{value}</p>
            <p className={styles.headlineStatsLabel}>{label}</p>
        </div>
    );

const CampaignDetails = () => {
    const { id } = useParams<{ id?: string }>();
    const campaignId = Number.isInteger(Number(id)) ? Number(id) : null;

    const { currencies } = useNonNullContext(OptionsContext);

    const {
        campaign,
        isInitialized: isCampaignInitialized,
        hasErrorLoading: bookingsHasErrorLoading,
    } = useCampaignDetails(campaignId);

    const {
        bookings,
        isInitialized: isBookingsInitialized,
        hasErrorLoading: campaignHasErrorLoading,
    } = useCampaignBookings(campaignId);

    const tiktokUserIds = useMemo(
        () =>
            uniq(
                bookings
                    .filter((booking) => booking.platform === 'TIKTOK')
                    .map((booking) => booking.influencer.tiktok_user_id)
                    .filter((id): id is number => id !== null)
            ),
        [bookings]
    );
    const instagramUserIds = useMemo(
        () =>
            uniq(
                bookings
                    .filter((booking) => booking.platform === 'INSTAGRAM')
                    .map((booking) => booking.influencer.instagram_user_id)
                    .filter((id): id is number => id !== null)
            ),
        [bookings]
    );

    const {
        tiktokUserImages,
        instagramUserImages,
        isActive: isInfluencersImagesActive,
        activate: activateInfluencerImages,
    } = useInfluencerImages(tiktokUserIds, instagramUserIds);

    useEffect(() => {
        if (!isBookingsInitialized || isInfluencersImagesActive) {
            return;
        }
        activateInfluencerImages();
    }, [isBookingsInitialized, isInfluencersImagesActive, activateInfluencerImages]);

    const { selectedPlatforms, totalCreators, totalCost, totalLiveCost, tiktokCreatorsCount, instagramCreatorsCount } =
        useMemo(() => calculateBookingsTotals(bookings), [bookings]);

    const {
        postStats: tiktokPostStats,
        postStatsTotals: tiktokPostStatsTotals,
        isInitialized: isTiktokPostStatsInitialized,
        hasErrorLoading: tiktokPostStatsHasErrorLoading,
        init: initTiktokPostStats,
    } = useCampaignTiktokPostStats(campaignId, tiktokCreatorsCount);

    useAbortableEffect(
        (signal) => {
            if (isBookingsInitialized && !isTiktokPostStatsInitialized) {
                initTiktokPostStats({ signal });
            }
        },
        [isBookingsInitialized, isTiktokPostStatsInitialized, initTiktokPostStats]
    );

    const {
        postStats: instagramPostStats,
        postStatsTotals: instagramPostStatsTotals,
        isInitialized: isInstagramPostStatsInitialized,
        hasErrorLoading: instagramPostStatsHasErrorLoading,
        init: initInstagramPostStats,
    } = useCampaignInstagramPostStats(campaignId, instagramCreatorsCount);

    useAbortableEffect(
        (signal) => {
            if (isBookingsInitialized && !isInstagramPostStatsInitialized) {
                initInstagramPostStats({ signal });
            }
        },
        [isBookingsInitialized, isInstagramPostStatsInitialized, initInstagramPostStats]
    );

    const tiktokPostStatsTableRows: TiktokPostStatsTableRow[] = useMemo(
        () =>
            bookings
                .filter((booking) => booking.platform === 'TIKTOK')
                .map((booking) => {
                    const postStats = tiktokPostStats.find((postStats) => postStats.booking_id === booking.id) || null;
                    const userImage =
                        tiktokUserImages.find(
                            (userImage) => userImage.user_id === booking.influencer?.tiktok_user_id
                        ) || null;

                    return {
                        booking,
                        ...postStats,
                        userImage,
                    };
                })
                .sort(sortTiktokPostStatsTableRows),
        [bookings, tiktokPostStats, tiktokUserImages]
    );

    const instagramPostStatsTableRows: InstagramPostStatsTableRow[] = useMemo(
        () =>
            bookings
                .filter((booking) => booking.platform === 'INSTAGRAM')
                .map((booking) => {
                    const postStats =
                        instagramPostStats.find((postStats) => postStats.booking_id === booking.id) || null;
                    const userImage =
                        instagramUserImages.find(
                            (userImage) => userImage.user_id === booking.influencer?.instagram_user_id
                        ) || null;

                    return {
                        booking,
                        ...postStats,
                        userImage,
                    };
                })
                .sort(sortInstagramPostStatsTableRows),
        [bookings, instagramPostStats, instagramUserImages]
    );

    const totalViews = (tiktokPostStatsTotals?.play_count || 0) + (instagramPostStatsTotals?.total_views || 0);
    const totalLikes = (tiktokPostStatsTotals?.digg_count || 0) + (instagramPostStatsTotals?.likes || 0);

    const campaignCurrency = currencies.find((currency) => currency.id === campaign?.currency);
    const cpm = (totalLiveCost / totalViews) * 1000;

    const cpmDisplayValue =
        campaignCurrency?.symbol && !isNaN(cpm) && isFinite(cpm)
            ? `${campaignCurrency.symbol}${numberWithCommas(cpm.toFixed(2))}`
            : null;

    const brief = bookings.find((booking) => booking.brief !== null)?.brief || 'No brief provided';

    const bookerName = campaign?.client_user_user_name ?? '-';
    const bookerEmail = campaign?.client_user_email ?? '-';

    const isInstagramTableLoading =
        (!isInstagramPostStatsInitialized && !instagramPostStatsHasErrorLoading) ||
        (!isBookingsInitialized && !bookingsHasErrorLoading);

    const isTiktokTableLoading =
        (!isTiktokPostStatsInitialized && !tiktokPostStatsHasErrorLoading) ||
        (!isBookingsInitialized && !bookingsHasErrorLoading);

    const isCampaignLoading = !isCampaignInitialized && !campaignHasErrorLoading;

    const hasError = bookingsHasErrorLoading || campaignHasErrorLoading || typeof campaignId !== 'number';

    return (
        <>
            <Helmet title={campaign?.name ?? `Campaign Details`} />
            {hasError ? (
                <div className={styles.errorMessage}>
                    <h1>Something went wrong</h1>
                    <p>Please try again later</p>
                </div>
            ) : (
                <div className={styles.container}>
                    <h1 className={styles.campaignTitle}>{isCampaignLoading ? <Skeleton /> : campaign?.name}</h1>
                    <div className={styles.sectionsContainer}>
                        <section className={styles.overview}>
                            <SectionTitle className={styles.title}>Campaign Results: Overview</SectionTitle>
                            <div className={styles.sectionBody}>
                                <HeadlineStatsCard
                                    value={bookings.length}
                                    label="Creator Posts"
                                    loading={!isBookingsInitialized}
                                />
                                <HeadlineStatsCard
                                    value={formatNumberToKNotation(totalViews)}
                                    label="Total Views"
                                    loading={!isInstagramPostStatsInitialized || !isTiktokPostStatsInitialized}
                                />
                                <HeadlineStatsCard
                                    value={formatNumberToKNotation(totalLikes)}
                                    label="Total Likes"
                                    loading={!isInstagramPostStatsInitialized || !isTiktokPostStatsInitialized}
                                />
                                <HeadlineStatsCard
                                    value={cpmDisplayValue ?? '-'}
                                    label="Campaign CPM"
                                    loading={
                                        !isCampaignInitialized ||
                                        !isInstagramPostStatsInitialized ||
                                        !isTiktokPostStatsInitialized
                                    }
                                />
                            </div>
                        </section>

                        <section className={styles.bookerDetails}>
                            <SectionTitle className={styles.title}>Booker Details</SectionTitle>
                            <div className={styles.sectionBody}>
                                <div className={styles.column}>
                                    <span className={styles.detailsLabel}>Name</span>
                                    <span className={styles.detailsValue}>
                                        {isCampaignLoading ? <Skeleton /> : bookerName}
                                    </span>
                                </div>

                                <div className={styles.column}>
                                    <span className={styles.detailsLabel}>Email</span>
                                    <span className={styles.detailsValue}>
                                        {isCampaignLoading ? <Skeleton /> : bookerEmail}
                                    </span>
                                </div>
                            </div>
                        </section>

                        <section className={styles.campaignResults}>
                            <SectionTitle className={styles.title}>Campaign Results: Detailed</SectionTitle>
                            <div className={styles.sectionBody}>
                                <div>
                                    <div className={styles.tableHeader}>
                                        <TiktokIcon />
                                        <h2 className={styles.tableHeader}>TikTok Creators Results</h2>
                                    </div>

                                    <div className={styles.tableScrollContainer}>
                                        <TiktokPostStatsTable
                                            loading={isTiktokTableLoading}
                                            data={tiktokPostStatsTableRows}
                                            totals={tiktokPostStatsTotals}
                                            error={tiktokPostStatsHasErrorLoading}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div className={styles.tableHeader}>
                                        <InstagramIcon />
                                        <h2 className={styles.tableHeader}>Instagram Creators Results</h2>
                                    </div>

                                    <div className={styles.tableScrollContainer}>
                                        <InstagramPostStatsTable
                                            loading={isInstagramTableLoading}
                                            data={instagramPostStatsTableRows}
                                            totals={instagramPostStatsTotals}
                                            error={instagramPostStatsHasErrorLoading}
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className={styles.bookingDetails}>
                            <SectionTitle className={styles.title}>Booking Details</SectionTitle>
                            <div className={styles.sectionBody}>
                                <div className={styles.bookingSummaryWrapper}>
                                    <BookingSummary
                                        loading={!isBookingsInitialized || !isCampaignInitialized}
                                        totalCost={totalCost}
                                        totalCreators={totalCreators}
                                        platforms={selectedPlatforms}
                                        currencyId={campaign?.currency}
                                    />
                                </div>
                                <span className={styles.detailsLabel}>Campaign Details</span>
                                {!isBookingsInitialized ? (
                                    <>
                                        <Skeleton />
                                        <Skeleton />
                                        <Skeleton />
                                    </>
                                ) : (
                                    <p className={styles.brief}>{brief}</p>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            )}
        </>
    );
};

export default CampaignDetails;
